import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "menu", "search", "list", "hiddenInput", "triggerLabel"]
  static values = { url: String, selected: String }

  connect() {
    this.campaigns = []
    this.isOpen = false
    this._onClickOutside = this.closeOnClickOutside.bind(this)
    this._onKeydown = this.closeOnEscape.bind(this)
    this.hiddenInputTarget.addEventListener("reset", () => this.resetTrigger())
    this.fetchCampaigns()
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    document.removeEventListener("keydown", this._onKeydown)
  }

  async fetchCampaigns() {
    try {
      const res = await fetch(this.urlValue)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      this.campaigns = json.data || []
      this.renderList(this.campaigns)
      if (this.selectedValue) this.applyInitialSelection()
    } catch (e) {
      console.error("[campaign-dropdown] Failed to load campaigns:", e)
      this.listTarget.innerHTML = this.emptyMessage("Failed to load campaigns")
    }
  }

  toggle(event) {
    event.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    this.isOpen = true
    this.searchTarget.value = ""
    this.renderList(this.campaigns)
    requestAnimationFrame(() => this.searchTarget.focus())
    document.addEventListener("click", this._onClickOutside)
    document.addEventListener("keydown", this._onKeydown)
  }

  close() {
    this.menuTarget.classList.add("hidden")
    this.isOpen = false
    document.removeEventListener("click", this._onClickOutside)
    document.removeEventListener("keydown", this._onKeydown)
  }

  search() {
    const q = this.searchTarget.value.toLowerCase().trim()
    if (!q) {
      this.renderList(this.campaigns)
      return
    }
    const filtered = this.campaigns.filter(c => {
      const fields = [
        c.name, c.ad_account_name, c.friendly_objective, c.objective,
        c.fb_campaign_id, c.fb_ad_account_id
      ].map(f => (f || "").toLowerCase())
      return fields.some(f => f.includes(q))
    })
    this.renderList(filtered)
  }

  select(event) {
    const row = event.currentTarget
    const id = row.dataset.campaignId
    const name = row.dataset.campaignName
    const acctName = row.dataset.adAccountName
    this.hiddenInputTarget.value = id
    this.selectedValue = id
    this.updateTrigger(name, acctName)
    this.close()
  }

  clear() {
    this.hiddenInputTarget.value = ""
    this.selectedValue = ""
    this.resetTrigger()
    this.close()
  }

  // --- Rendering ---

  renderList(campaigns) {
    if (!campaigns.length) {
      this.listTarget.innerHTML = this.emptyMessage("No campaigns found")
      return
    }

    const withData = campaigns.filter(c => c.has_data)
    const withoutData = campaigns.filter(c => !c.has_data)

    let html = ""

    if (withData.length > 0) {
      html += this.sectionHeader("With report data", withData.length)
      html += withData.map(c => this.campaignRow(c)).join("")
    }

    if (withoutData.length > 0) {
      html += this.sectionHeader("All campaigns", withoutData.length)
      html += withoutData.map(c => this.campaignRow(c)).join("")
    }

    this.listTarget.innerHTML = html
  }

  campaignRow(c) {
    const name = c.name || "Unnamed Campaign"
    const acctName = c.ad_account_name || c.fb_ad_account_id || ""
    // AB-066 — prefer the server-resolved ObjectiveResolver label
    // (`friendly_objective`) so raw OUTCOME_* strings never reach the
    // customer-facing dropdown (CLAUDE rule 4). Fall back to the
    // legacy formatter only if the server omits the field.
    const objective = c.friendly_objective || this.formatObjective(c.objective)
    const status = this.statusBadge(c.status)
    const selected = c.fb_campaign_id === this.selectedValue
    const dataIndicator = c.has_data
      ? '<span class="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>'
      : ''

    return `<div class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${selected ? "bg-gray-50 dark:bg-gray-800/50" : ""}"
                 data-action="click->campaign-dropdown#select"
                 data-campaign-id="${this.esc(c.fb_campaign_id)}"
                 data-campaign-name="${this.esc(name)}"
                 data-ad-account-name="${this.esc(acctName)}">
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-1.5 min-w-0">
          ${dataIndicator}
          <span class="text-sm font-medium text-gray-900 dark:text-white truncate">${this.esc(name)}</span>
        </div>
        ${status}
      </div>
      <div class="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5 ${c.has_data ? "ml-3" : ""}">
        <span class="truncate">${this.esc(acctName)}</span>
        ${objective ? `<span class="mx-0.5">&middot;</span><span class="truncate">${this.esc(objective)}</span>` : ""}
      </div>
    </div>`
  }

  sectionHeader(title, count) {
    return `<div class="sticky top-0 px-3 py-1.5 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 backdrop-blur-sm z-10">
      <span class="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">${this.esc(title)}</span>
      <span class="text-[10px] text-gray-400 dark:text-gray-500 ml-1">(${count})</span>
    </div>`
  }

  updateTrigger(name, acctName) {
    const label = this.triggerLabelTarget
    label.innerHTML = `<span class="text-sm font-medium text-gray-900 dark:text-white truncate">${this.esc(name)}</span>`
    if (acctName) {
      label.innerHTML += `<span class="text-xs text-gray-500 dark:text-gray-400 truncate">${this.esc(acctName)}</span>`
    }
    label.classList.add("flex", "flex-col", "items-start", "leading-tight")
  }

  resetTrigger() {
    const label = this.triggerLabelTarget
    label.textContent = "All campaigns"
    label.classList.remove("flex", "flex-col", "items-start", "leading-tight")
  }

  applyInitialSelection() {
    const campaign = this.campaigns.find(c => c.fb_campaign_id === this.selectedValue)
    if (campaign) {
      this.updateTrigger(
        campaign.name || "Unnamed Campaign",
        campaign.ad_account_name || campaign.fb_ad_account_id || ""
      )
    }
  }

  // --- Helpers ---

  statusBadge(status) {
    const s = (status || "").toLowerCase()
    if (s === "active") return '<span class="badge-active text-[10px]">Active</span>'
    if (s === "paused") return '<span class="badge-paused text-[10px]">Paused</span>'
    const label = status ? status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) : "Unknown"
    return `<span class="badge-muted text-[10px]">${this.esc(label)}</span>`
  }

  formatObjective(obj) {
    if (!obj) return ""
    return obj.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
  }

  emptyMessage(message) {
    return `<div class="px-3 py-4 text-center text-xs text-gray-400 dark:text-gray-500">${this.esc(message)}</div>`
  }

  esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }
}
